/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/utils/feature 目录；
 * 2. 提供特征命令依赖解析所需的纯函数；
 * 3. FeatureCommandAdapter 及具体特征适配器复用这里的工具，避免删除计划器散落解析命令字段。
 */

/**
 * 判断值是否为普通对象记录。
 *
 * @param value 待判断值。
 * @returns 是否为普通对象记录。
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/**
 * 读取命令参数中的字符串字段。
 *
 * @param command 待解析命令。
 * @param key 参数字段名。
 * @returns 字符串值；不存在时返回 null。
 */
export function readCommandStringParam(command: CadCommand, key: string): string | null {
    const params = isRecord(command.params) ? command.params : {};
    const value = params[key];

    return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * 读取命令参数中的字符串数组字段。
 *
 * @param command 待解析命令。
 * @param key 参数字段名。
 * @returns 过滤空值后的字符串数组。
 */
export function readCommandStringArrayParam(command: CadCommand, key: string): string[] {
    const params = isRecord(command.params) ? command.params : {};
    const value = params[key];
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

/**
 * 去重并过滤空字符串。
 *
 * @param values 候选字符串集合。
 * @returns 按首次出现顺序去重后的字符串列表。
 */
export function uniqueNonEmptyStrings(values: Iterable<string | null | undefined>): string[] {
    const result = new Set<string>();

    Array.from(values).forEach((value) => {
        if (typeof value === 'string' && value.length > 0) {
            result.add(value);
        }
    });

    return Array.from(result);
}

/**
 * 可用于判断特征引用关系的轻量几何对象。
 *
 * 设计说明：
 * 1. Worker 摘要、正式 GeometryObject 与测试桩只要携带这些字段即可参与判断；
 * 2. 这里不要求完整 GeometryObject，避免 Viewer 增量同步为了复用工具函数而做无意义类型转换；
 * 3. 该类型只描述只读依赖分析所需字段，不参与对象生命周期管理。
 */
export interface FeatureReferenceGeometryObject {
    /** 几何对象 ID。 */
    readonly id: string;
    /** 生成该对象的命令 ID。 */
    readonly commandId?: string;
    /** 对象元数据。 */
    readonly metadata?: unknown;
}

/**
 * 收集 topology provenance 中出现过的特征 ID。
 *
 * @param entries provenance 子项列表。
 * @param target 目标集合。
 */
function collectTopologyEntryFeatureIds(entries: unknown, target: Set<string>): void {
    if (!Array.isArray(entries)) {
        return;
    }

    entries.forEach((entry) => {
        if (!isRecord(entry)) {
            return;
        }
        const featureId = entry.generatingFeatureId;
        if (typeof featureId === 'string' && featureId.length > 0) {
            target.add(featureId);
        }
    });
}

/**
 * 判断几何对象是否是指定特征直接产出的正式结果。
 *
 * 设计说明：
 * topology provenance 会让最终合并体同时引用多个历史特征，但只有对象 ID、commandId 或
 * metadata.featureId 直接命中时，该对象的来源修订才能反向补充到对应命令的输入依赖中。
 *
 * @param object 几何对象或 Worker 摘要对象。
 * @param featureId 特征 ID。
 * @returns 是否为该特征的直接结果。
 */
export function isGeometryObjectDirectResultOfFeature(
    object: FeatureReferenceGeometryObject,
    featureId: string,
): boolean {
    const normalizedFeatureId = featureId.trim();
    if (normalizedFeatureId.length <= 0) {
        return false;
    }

    const metadata = isRecord(object.metadata) ? object.metadata : {};
    return object.id === normalizedFeatureId
        || object.commandId === normalizedFeatureId
        || metadata.featureId === normalizedFeatureId;
}

/**
 * 收集正式结果元数据声明的直接上游模型修订引用。
 *
 * 设计说明：
 * 1. `consumedModelBodyRevisionIds` 是布尔特征替换旧实体修订的权威声明；
 * 2. `sourceTargetId/sourceObjectId` 保留直接输入体，供历史编辑和删除依赖闭包使用；
 * 3. `mergedSourceIds` 表达合并结果所吸收的输入体，也属于精确的模型依赖；
 * 4. 不读取稳定 `modelBodyId`，避免同一逻辑实体的历史修订互相形成伪依赖。
 *
 * @param object 几何对象或 Worker 摘要对象。
 * @returns 去重后的直接上游模型引用。
 */
export function collectGeometryObjectSourceReferenceIds(
    object: FeatureReferenceGeometryObject,
): string[] {
    const metadata = isRecord(object.metadata) ? object.metadata : {};
    return uniqueNonEmptyStrings([
        typeof metadata.sourceTargetId === 'string' ? metadata.sourceTargetId : null,
        typeof metadata.sourceObjectId === 'string' ? metadata.sourceObjectId : null,
        ...(Array.isArray(metadata.consumedModelBodyRevisionIds)
            ? metadata.consumedModelBodyRevisionIds.filter((value): value is string => typeof value === 'string')
            : []),
        ...(Array.isArray(metadata.mergedSourceIds)
            ? metadata.mergedSourceIds.filter((value): value is string => typeof value === 'string')
            : []),
    ]);
}

/**
 * 收集几何对象显式或通过拓扑出处引用到的特征 ID。
 *
 * 设计说明：
 * 1. 相交实体合并后，一个结果体可能同时携带多个特征的 topology provenance；
 * 2. 回退控制棒从合并状态退回到上游前缀时，命令本身没变的上游特征也可能从 merged body
 *    还原为独立 body，因此增量回灌必须能从对象引用集合识别这种“合并/解合并”变化；
 * 3. 这里只读取 plain metadata，不触碰 THREE/Puppy CAD 内核 运行时对象，供 Viewer 和测试共享同一套判断语义。
 *
 * @param object 几何对象或 Worker 摘要对象。
 * @returns 按首次出现顺序去重后的特征 ID 列表。
 */
export function collectGeometryObjectReferencedFeatureIds(
    object: FeatureReferenceGeometryObject,
): string[] {
    const result = new Set<string>();
    if (typeof object.id === 'string' && object.id.length > 0) {
        result.add(object.id);
    }
    if (typeof object.commandId === 'string' && object.commandId.length > 0) {
        result.add(object.commandId);
    }

    const metadata = isRecord(object.metadata) ? object.metadata : {};
    if (typeof metadata.featureId === 'string' && metadata.featureId.length > 0) {
        result.add(metadata.featureId);
    }
    collectGeometryObjectSourceReferenceIds(object).forEach((referenceId) => result.add(referenceId));

    const provenance = metadata.topologyProvenance;
    if (isRecord(provenance)) {
        collectTopologyEntryFeatureIds(provenance.faces, result);
        collectTopologyEntryFeatureIds(provenance.edges, result);
        collectTopologyEntryFeatureIds(provenance.vertices, result);
    }

    return [...result];
}

/**
 * 判断几何对象是否引用指定特征。
 *
 * @param object 几何对象或 Worker 摘要对象。
 * @param featureId 特征 ID。
 * @returns 是否引用该特征。
 */
export function doesGeometryObjectReferenceFeature(
    object: FeatureReferenceGeometryObject,
    featureId: string,
): boolean {
    const normalizedFeatureId = featureId.trim();
    if (normalizedFeatureId.length <= 0) {
        return false;
    }

    return collectGeometryObjectReferencedFeatureIds(object).includes(normalizedFeatureId);
}

/**
 * 判断几何对象是否引用任一指定特征。
 *
 * @param object 几何对象或 Worker 摘要对象。
 * @param featureIds 特征 ID 集合。
 * @returns 是否引用任一特征。
 */
export function doesGeometryObjectReferenceAnyFeature(
    object: FeatureReferenceGeometryObject,
    featureIds: Iterable<string>,
): boolean {
    for (const featureId of featureIds) {
        if (doesGeometryObjectReferenceFeature(object, featureId)) {
            return true;
        }
    }
    return false;
}

/**
 * 收集 Worker 摘要中归属于指定特征的结果对象。
 *
 * 设计说明：
 * 1. 命令 ID、对象 commandId 与 metadata.featureId 都可能是历史阶段留下的稳定关联；
 * 2. 合并实体的最终 commandId 会指向宿主特征，必须继续通过 topology provenance 找回被合并特征；
 * 3. 删除依赖图和设计树投影都依赖这些产物 ID，才能沿模型引用找到下游特征并做局部高亮；
 * 4. 这里只消费摘要数据，不触碰 Three.js 运行时对象，避免 Vue 或 UI 层代理 3D 对象。
 *
 * @param featureId 特征 ID。
 * @param objects 当前 Worker 对象摘要。
 * @returns 对象 ID 列表。
 */
export function collectCurrentFeatureResultObjectIds(
    featureId: string,
    objects: readonly GeometryObject[],
): string[] {
    return objects
        .filter((object) => doesGeometryObjectReferenceFeature(object, featureId))
        .map((object) => object.id);
}
